import { useEventStore } from '@/stores/eventStore'
import type { useLibraryStore } from '@/stores/libraryStore'
import type { useWorkspaceStore, TrackRouteDraftCommitInput } from '@/stores/workspaceStore'
import type { Segment, Tour } from '@/types/tour'

export type TrackCreateInput = {
  clientId?: string
  name: string
  segments: Segment[]
  color?: string
  routingProfile?: string
  type?: string
}

export type RoutePointCreateInput = {
  clientId?: string
  lat: number
  lon: number
  ele?: number
  name?: string
  description?: string
  order: number
  trackClientId: string
}

export type RoutePointInsertInput = {
  clientId?: string
  lat: number
  lon: number
  ele?: number
  name?: string
  description?: string
  trackClientId?: string
}

export interface TrackRoutingAdapter {
  resolveTargetTourId: () => string | null
  getTour: (tourId: string) => Tour | null
  runInSyncBatch?: <T>(operation: () => Promise<T>) => Promise<T>
  addTrack: (tourId: string, data: TrackCreateInput) => Promise<string | null>
  deleteTrack: (tourId: string, trackClientId: string) => Promise<void>
  addRoutePoint: (tourId: string, data: RoutePointCreateInput) => Promise<void>
  insertRoutePointAt: (
    tourId: string,
    insertIndex: number,
    data: RoutePointInsertInput,
  ) => Promise<void>
  insertRoutePointsAt?: (
    tourId: string,
    insertIndex: number,
    data: RoutePointInsertInput[],
  ) => Promise<void>
  deleteRoutePoint: (tourId: string, routePointIndex: number) => Promise<void>
  updateRoutePointPosition: (
    tourId: string,
    routePointIndex: number,
    newLat: number,
    newLon: number,
  ) => Promise<void>
  reorderTrackRoutePoints?: (
    tourId: string,
    trackClientId: string,
    orderedRoutePointClientIds: string[],
  ) => Promise<void>
  replaceSegments: (
    tourId: string,
    trackClientId: string,
    deletedIndices: number[],
    newSegments: { segment: Segment; order: number }[],
    options?: { skipLineStringRebuild?: boolean },
  ) => Promise<void>
  replaceAllSegments: (
    tourId: string,
    trackClientId: string,
    oldSegmentClientIds: string[],
    newSegments: Segment[],
  ) => Promise<void>
  commitTrackRouteDraft?: (input: TrackRouteDraftCommitInput) => Promise<void>
  onLineStringUpdated?: (tourId: string, trackClientId: string, lineString: string) => void
  onDrawingDiscard?: () => void
  onExit?: () => void
}

export function createWorkspaceTrackRoutingAdapter(options: {
  workspaceStore: ReturnType<typeof useWorkspaceStore>
  resolveTargetTourId: () => string | null
  onExit?: () => void
}): TrackRoutingAdapter {
  const { workspaceStore, resolveTargetTourId, onExit } = options

  return {
    resolveTargetTourId,
    getTour: (tourId) => workspaceStore.getTourById(tourId) ?? null,
    runInSyncBatch: (operation) => useEventStore().runInSyncBatch(operation),
    addTrack: (tourId, data) => workspaceStore.addTrack(tourId, data),
    deleteTrack: async (tourId, trackClientId) => {
      const tour = workspaceStore.getTourById(tourId)
      if (!tour) return
      const trackIndex = tour.tracks.findIndex((track) => track.clientId === trackClientId)
      if (trackIndex < 0) return
      await workspaceStore.deleteTrack(tourId, trackIndex)
    },
    addRoutePoint: async (tourId, data) => {
      await workspaceStore.addRoutePoint(tourId, data)
    },
    insertRoutePointAt: async (tourId, insertIndex, data) => {
      await workspaceStore.insertRoutePointAt(tourId, insertIndex, data)
    },
    insertRoutePointsAt: async (tourId, insertIndex, data) => {
      await workspaceStore.insertRoutePointsAt(tourId, insertIndex, data)
    },
    deleteRoutePoint: (tourId, routePointIndex) =>
      workspaceStore.deleteRoutePoint(tourId, routePointIndex),
    updateRoutePointPosition: (tourId, routePointIndex, newLat, newLon) =>
      workspaceStore.updateRoutePointPosition(tourId, routePointIndex, newLat, newLon),
    reorderTrackRoutePoints: (tourId, trackClientId, orderedRoutePointClientIds) =>
      workspaceStore.reorderTrackRoutePoints(tourId, trackClientId, orderedRoutePointClientIds),
    replaceSegments: (tourId, trackClientId, deletedIndices, newSegments, optionsArg) =>
      workspaceStore.replaceSegments(
        tourId,
        trackClientId,
        deletedIndices,
        newSegments,
        optionsArg,
      ),
    replaceAllSegments: (tourId, trackClientId, oldSegmentClientIds, newSegments) =>
      workspaceStore.replaceAllSegments(tourId, trackClientId, oldSegmentClientIds, newSegments),
    commitTrackRouteDraft: (input) => workspaceStore.commitTrackRouteDraft(input),
    onExit,
  }
}

export function createLibraryTrackRoutingAdapter(options: {
  libraryStore: ReturnType<typeof useLibraryStore>
  resolveTargetTourId: () => string | null
  onExit?: () => void
}): TrackRoutingAdapter {
  const { libraryStore, resolveTargetTourId, onExit } = options

  return {
    resolveTargetTourId,
    getTour: (tourId) => {
      const tour = libraryStore.detailTour
      return tour && tour.clientId === tourId ? tour : null
    },
    runInSyncBatch: (operation) => useEventStore().runInSyncBatch(operation),
    addTrack: (tourId, data) => libraryStore.addTrackToTour(tourId, data),
    deleteTrack: (tourId, trackClientId) => libraryStore.deleteTrack(tourId, trackClientId),
    addRoutePoint: async (tourId, data) => {
      await libraryStore.addRoutePointToTour(tourId, data)
    },
    insertRoutePointAt: async (tourId, insertIndex, data) => {
      await libraryStore.insertRoutePointAt(tourId, insertIndex, data)
    },
    insertRoutePointsAt: async (tourId, insertIndex, data) => {
      await libraryStore.insertRoutePointsAt(tourId, insertIndex, data)
    },
    deleteRoutePoint: (tourId, routePointIndex) =>
      libraryStore.deleteRoutePoint(tourId, routePointIndex),
    updateRoutePointPosition: (tourId, routePointIndex, newLat, newLon) =>
      libraryStore.updateRoutePointPosition(tourId, routePointIndex, newLat, newLon),
    reorderTrackRoutePoints: (tourId, trackClientId, orderedRoutePointClientIds) =>
      libraryStore.reorderTrackRoutePoints(tourId, trackClientId, orderedRoutePointClientIds),
    replaceSegments: (tourId, trackClientId, deletedIndices, newSegments, optionsArg) =>
      libraryStore.replaceSegments(tourId, trackClientId, deletedIndices, newSegments, optionsArg),
    replaceAllSegments: (tourId, trackClientId, oldSegmentClientIds, newSegments) =>
      libraryStore.replaceAllSegments(tourId, trackClientId, oldSegmentClientIds, newSegments),
    onLineStringUpdated: (tourId, trackClientId, lineString) => {
      const tourListItem = libraryStore.tours.find((tour) => tour.clientId === tourId)
      if (!tourListItem) return
      const track = tourListItem.tracks.find((candidate) => candidate.clientId === trackClientId)
      if (track) {
        track.pointsLineReduced500m = lineString
      }
    },
    commitTrackRouteDraft: (input) => libraryStore.commitTrackRouteDraft(input),
    onExit,
  }
}
